import React from 'react';
import { Calendar, Video, Clock, ExternalLink, Plus } from 'lucide-react';
import { MentorAreaConfig, MentorEvent } from '../../types/mentorship';

interface MentorshipEventsPageProps {
  config: MentorAreaConfig;
  isMentorOrAdmin?: boolean;
  onOpenAdminPanel?: () => void;
}

export const MentorshipEventsPage: React.FC<MentorshipEventsPageProps> = ({
  config,
  isMentorOrAdmin = false,
  onOpenAdminPanel,
}) => {
  const events: MentorEvent[] = config.events || [
    {
      id: 'ev-1',
      title: 'Call de Mentoria Semanal: Estratégia & Tira-Dúvidas Ao Vivo',
      dateText: 'Toda Quinta-feira',
      timeText: '20:00 (Horário de Brasília)',
      description: 'Encontro semanal exclusivo para alunos tirarem dúvidas ao vivo, analisarem produtos e revisarem estratégias.',
      meetingUrl: 'https://zoom.us',
      isLive: true,
    },
    {
      id: 'ev-2',
      title: 'Workshop Especial: Automações com n8n & IA',
      dateText: '20 de Agosto',
      timeText: '19:30',
      description: 'Sessão prática de construção de agentes e automação de processos para a sua operação.',
      meetingUrl: 'https://meet.google.com',
      isLive: false,
    },
  ];

  const accentColor = config.branding.accentColor || '#0FFF55';

  return (
    <div className="w-full min-h-[70vh] flex flex-col gap-6 animate-fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
            <Calendar className="w-4 h-4" style={{ color: accentColor }} />
            <span>Calendário Exclusivo</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black text-white uppercase tracking-tight">
            Eventos & Chamadas ao Vivo
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Participe dos encontros ao vivo, workshops e sessões de tira-dúvidas com {config.mentorName}.
          </p>
        </div>

        {isMentorOrAdmin && onOpenAdminPanel && (
          <button
            onClick={onOpenAdminPanel}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs bg-[#0FFF55] hover:bg-[#0dd149] text-black shadow-lg transition-all active:scale-95 flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span>Novo Evento</span>
          </button>
        )}
      </div>

      {/* Events List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
        {events.map((ev) => (
          <div
            key={ev.id}
            className="relative bg-[#17181c] border border-white/10 rounded-2xl p-6 flex flex-col justify-between gap-4 hover:border-white/20 transition-all duration-300 shadow-xl group"
          >
            <div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <span
                  className="px-3 py-1 rounded-full text-[11px] font-extrabold uppercase border border-white/10 flex items-center gap-1.5"
                  style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                >
                  <Clock className="w-3 h-3" />
                  {ev.dateText} {ev.timeText ? `• ${ev.timeText}` : ''}
                </span>

                {ev.isLive && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] font-black uppercase animate-pulse">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Ao Vivo
                  </span>
                )}
              </div>

              <h3 className="text-lg md:text-xl font-bold text-white mb-2 leading-snug group-hover:text-[#0FFF55] transition-colors">
                {ev.title}
              </h3>
              <p className="text-gray-400 text-xs leading-relaxed">{ev.description}</p>
            </div>

            {ev.meetingUrl && (
              <a
                href={ev.meetingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-extrabold text-xs bg-white/10 hover:bg-white/20 text-white border border-white/15 transition-all active:scale-95 mt-2"
              >
                <Video className="w-4 h-4 text-[#0FFF55]" />
                <span>Entrar na Reunião</span>
                <ExternalLink className="w-3 h-3 text-gray-400" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
